import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { useCreateVersion } from '@features/assessment';
import { useFeatureFlag } from '@hooks';
import { FeatureFlagKey } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import { ArrowRightIcon, PlusCircleIcon } from '@phosphor-icons/react';

// Deep import rather than through `@features/remediation`: that barrel pulls in
// screens which import `@features/assessment`, and going through it here would
// close the loop.
import { useActiveRemediationRuns } from '../../../remediation/api/remediation-api';

interface StartNewVersionConfirmDialogProps {
  assessment: Assessment;
  open: boolean;
  onClose: () => void;
  /** When true, jump straight into the stepper for the new draft. Used from
   *  the overview "Start New Version" entry point so users land in the
   *  workflow instead of a stale results page. */
  navigateToWorkflow?: boolean;
}

export const StartNewVersionConfirmDialog: FC<StartNewVersionConfirmDialogProps> = observer(
  ({ assessment, open, onClose, navigateToWorkflow = false }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTrans();
    const { mutate: createVersion, isPending: isCreatingVersion } = useCreateVersion();
    const isRemediationEnabled = useFeatureFlag(FeatureFlagKey.REMEDIATION);

    /**
     * A remediation run in flight on this lineage blocks a new version.
     *
     * The API already refuses it — `CreateVersionService` checks the same thing
     * under the lock it forks versions in, which is where it has to be, because
     * only a lock can order a manual version against the fork a finalize is
     * doing at that moment. What was missing is any of that reaching the user:
     * the button was offered, pressed, and answered with a 409 whose text never
     * made it to the screen.
     *
     * The run's whole open life counts, not just while the AI is writing.
     * `useActiveRemediationRuns` defaults to exactly the non-terminal set —
     * pending, processing, ready-for-review and finalizing — so a run sitting on
     * documents the user has not decided yet blocks too. That is the case worth
     * blocking: forking a version there strands the decisions, and the documents
     * would be applied to a version that is no longer the latest.
     *
     * Fetched only while the dialog is open and the feature is enabled for this
     * tenant; this is a confirmation step, not a screen, and the answer is stale
     * the moment it closes. Disabled outright when the flag is off — remediation
     * is unreachable then, so nothing it left behind should block a new version.
     */
    const { data: runs, isLoading: runsLoading } = useActiveRemediationRuns(
      open && isRemediationEnabled,
      undefined,
      false,
    );
    const rootId = assessment.parentAssessmentId ?? assessment.id;
    const blockingRun = (runs?.runs ?? []).find((run) => run.rootAssessmentId === rootId) ?? null;
    // Never optimistic: until the answer is in, the action stays out of reach
    // rather than briefly offering something the server will refuse.
    const blocked = runsLoading || Boolean(blockingRun);

    const handleConfirm = () => {
      createVersion(
        { assessmentId: assessment.id },
        {
          onSuccess: (data) => {
            if (navigateToWorkflow) {
              // New versions always start at domain index 1
              // (see assessment-dal.service.ts createNewVersion).
              navigate(routes.assessmentDomain(data.assessmentId, 1));
            }
          },
          onSettled: onClose,
        },
      );
    };

    return (
      <Dialog
        open={open}
        onClose={() => !isCreatingVersion && onClose()}
        slotProps={{
          paper: { sx: { borderRadius: 4, minWidth: 440, maxWidth: 520, p: 1 } },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: theme.palette.blue[100],
                color: theme.palette.accent.blue,
                flexShrink: 0,
              }}
            >
              <PlusCircleIcon size={24} weight="regular" />
            </Box>
            <Typography variant="h6" component="span" fontWeight={700} fontSize={18}>
              <Trans i18nKey="app.assessments.newVersion.title" />
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              color: theme.palette.text.secondary,
            }}
          >
            <Trans
              i18nKey="app.assessments.newVersion.confirmMessage"
              values={{ title: assessment.title }}
            />
          </Typography>

          {/* Why the action is out of reach, and the way to clear it. Shown
              instead of hiding the button: a control that vanishes teaches
              nothing, and "where did New version go" is a worse question than
              "what do I have to finish first". */}
          {blockingRun && (
            <Box sx={{ mt: 2 }}>
              <CalloutBanner
                tone="warning"
                filledIcon
                title={t('app.assessments.newVersion.blockedTitle')}
                action={
                  <Button
                    variant="secondary"
                    endIcon={<ArrowRightIcon size={15} />}
                    onClick={() => {
                      onClose();
                      navigate(routes.remediationRun(blockingRun.runId));
                    }}
                  >
                    {t('app.assessments.newVersion.openRemediation')}
                  </Button>
                }
              >
                {blockingRun.itemCounts.reviewable > 0
                  ? t('app.assessments.newVersion.blockedNeedsReview', {
                      count: blockingRun.itemCounts.reviewable,
                    })
                  : t('app.assessments.newVersion.blockedRunning')}
              </CalloutBanner>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={isCreatingVersion}
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.875,
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.grey[400],
              },
            }}
          >
            <Trans i18nKey="app.common.cancel" />
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disableElevation
            disabled={isCreatingVersion || blocked}
            startIcon={isCreatingVersion ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.875,
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.black[1100],
              },
            }}
          >
            {isCreatingVersion ? (
              <Trans i18nKey="app.assessments.newVersion.starting" />
            ) : (
              <Trans i18nKey="app.assessments.newVersion.confirm" />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export const canStartNewVersion = (
  assessment: Assessment,
  draftStatus: Assessment['status'],
) => {
  const familyHasDraft = (assessment.recentVersions ?? []).some(
    (v) => v.status === draftStatus && v.id !== assessment.id,
  );
  return assessment.isLatest && !familyHasDraft;
};
